import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import useMedia from '../../../hooks/useMedia';
import { useChat } from '../../../components/context/ChatContext';
import { AuthContext } from '../../../components/context/AuthContext';
import { META_MAX_FILE_SIZE, MB } from 'constants/utils';
import { X, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'react-hot-toast';
import InstagramFeedPreview from './InstagramFeedPreview';
import StoryPreview from './StoryPreview';
import WhatsAppConversationPreview from './WhatsAppConversationPreview';

const likes = Array.from({ length: 30 }, () => Math.floor(Math.random() * 900) + 800);

// format something like 2025-08-05 to 05/08/2025
const formatDateString = (dateString) => {
  if (!dateString) return 'No definida';
  if (!dateString.includes('-')) return dateString;
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const MetaPreview = ({ campaignPreview, isCampaignSent: _isCampaignSent }) => {
  const { currentCampaign } = useChat();
  const { auth } = useContext(AuthContext);
  const { user } = auth;
  const {
    loadingMedia,
    uploadingMedia,
    mediaFiles,
    mediaInputRef,
    getMedia,
    handleInputChange,
    removeMedia,
  } = useMedia(campaignPreview.id, 'meta');

  const [selectedFormat, setSelectedFormat] = useState('instagram_feed');
  const [editingHeadline, setEditingHeadline] = useState(null);
  const [editingDescription, setEditingDescription] = useState(null);
  const [editHeadlineText, setEditHeadlineText] = useState('');
  const [editDescriptionText, setEditDescriptionText] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOrientation, setImageOrientation] = useState('portrait'); // 'portrait', 'landscape', 'square'
  const [loadingImage, setLoadingImage] = useState(false);
  const [deletingMedia, setDeletingMedia] = useState(false);

  // Estados para edición de campos de campaña
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editLocations, setEditLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Lista de países disponibles
  const countries = [
    { code: 'UY', name: 'Uruguay' },
    { code: 'AR', name: 'Argentina' },
    { code: 'BR', name: 'Brasil' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'MX', name: 'México' },
    { code: 'PE', name: 'Perú' },
    { code: 'ES', name: 'España' },
    { code: 'US', name: 'Estados Unidos' },
    { code: 'CA', name: 'Canadá' },
  ];

  const catalogProducts = useMemo(() => currentCampaign?.catalog?.products || [], [currentCampaign?.catalog?.products]);
  const catalogImages = useMemo(() => 
    catalogProducts.filter(product => product.image_url), 
    [catalogProducts]
  );
  const hasCatalog = Boolean(currentCampaign?.meta_settings?.advantage_plus?.catalog_id);
  const isWhatsAppCampaign = currentCampaign?.campaign_type === 'WHATSAPP_BUSINESS';

  // Efecto para inicializar medios
  useEffect(() => {
    if (campaignPreview.id) {
      getMedia();
    }
  }, [campaignPreview.id, getMedia]);

  // Reset currentImageIndex when mediaFiles or catalogImages change
  useEffect(() => {
    if (hasCatalog) {
      // Handle catalog images
      if (catalogImages.length === 0) {
        setCurrentImageIndex(0);
      } else if (currentImageIndex >= catalogImages.length) {
        setCurrentImageIndex(0);
      }
    } else {
      // Handle uploaded media files
      if (mediaFiles.length === 0) {
        setCurrentImageIndex(0);
      } else if (currentImageIndex >= mediaFiles.length) {
        setCurrentImageIndex(0);
      }
    }
  }, [hasCatalog, catalogImages.length, mediaFiles.length, currentImageIndex]);

  // Detect image orientation when primary image changes
  useEffect(() => {
    const detectImageOrientation = () => {
      let currentMediaUrl = null;
      if (hasCatalog && catalogImages.length > 0) {
        currentMediaUrl = catalogImages[currentImageIndex]?.image_url;
      } else if (mediaFiles.length > 0) {
        currentMediaUrl = mediaFiles[currentImageIndex]?.url;
      }

      if (!currentMediaUrl) {
        setImageOrientation('portrait');
        setLoadingImage(false);
        return;
      }

      // Check if it's a video - if so, don't detect orientation
      const url = currentMediaUrl || '';
      const extension = url.split('.').pop()?.toLowerCase();
      if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
        setImageOrientation('portrait'); // Default for videos
        setLoadingImage(false);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio > 1.1) {
          setImageOrientation('landscape');
        } else if (ratio < 0.9) {
          setImageOrientation('portrait');
        } else {
          setImageOrientation('square');
        }
        setLoadingImage(false);
      };
      img.onerror = () => {
        setImageOrientation('portrait');
        setLoadingImage(false);
      };
      img.src = currentMediaUrl;
    };

    detectImageOrientation();
  }, [currentImageIndex, hasCatalog, mediaFiles, catalogImages]);

  // Update currentImageIndex to the last uploaded item when new media is uploaded
  useEffect(() => {
    if (!hasCatalog && mediaFiles.length > 0) {
      // Set to the last uploaded item
      setCurrentImageIndex(mediaFiles.length - 1);
    }
  }, [mediaFiles.length, hasCatalog]);

  const adFormats = [
    { id: 'instagram_feed', name: 'Feed', icon: '📷' },
    { id: 'story_format', name: 'Story', icon: '📱' },
    ...(isWhatsAppCampaign ? [{ id: 'whatsapp_conversation', name: 'WhatsApp', icon: '💬' }] : []),
  ];

  // Function to handle image selection
  const handleImageSelection = () => {
    if (!hasCatalog) {
      // Add file size validation to the input
      const input = mediaInputRef.current;
      if (input) {
        input.onchange = (e) => {
          const files = Array.from(e.target.files);
          const oversizedFiles = files.filter(file => file.size > META_MAX_FILE_SIZE);
          
          if (oversizedFiles.length > 0) {
            toast.error(`Algunos archivos superan el límite de ${META_MAX_FILE_SIZE / MB}MB`);
            // Clear the input
            input.value = '';
            return;
          }
          
          // If all files are valid, proceed with normal upload
          handleInputChange(e);
        };
      }
      mediaInputRef.current?.click();
    }
  };

  // Navigation functions
  const nextImage = () => {
    setLoadingImage(true);
    if (hasCatalog) {
      // Navigate through catalog images
      const maxIndex = catalogImages.length - 1;
      setCurrentImageIndex(prev => prev < maxIndex ? prev + 1 : 0);
    } else {
      // Navigate through uploaded media files
      const maxIndex = mediaFiles.length - 1;
      setCurrentImageIndex(prev => prev < maxIndex ? prev + 1 : 0);
    }
  };

  const prevImage = () => {
    setLoadingImage(true);
    if (hasCatalog) {
      // Navigate through catalog images
      const maxIndex = catalogImages.length - 1;
      setCurrentImageIndex(prev => prev > 0 ? prev - 1 : maxIndex);
    } else {
      // Navigate through uploaded media files
      const maxIndex = mediaFiles.length - 1;
      setCurrentImageIndex(prev => prev > 0 ? prev - 1 : maxIndex);
    }
  };

  const removeCurrentImage = useCallback(async () => {
    if (hasCatalog) return; // Can't remove catalog images
    const indexAtDelete = currentImageIndex;
    const mediaCountAtDelete = mediaFiles.length;
    const currentImage = mediaFiles[indexAtDelete];
    if (!currentImage) return;
    setDeletingMedia(true);
    try {
      await removeMedia(currentImage.id);
      // If deleted item was the last visible one, move selection to previous item.
      if (indexAtDelete === mediaCountAtDelete - 1 && indexAtDelete > 0) {
        setCurrentImageIndex((prev) => prev - 1);
      }
    } finally {
      setDeletingMedia(false);
    }
  }, [hasCatalog, mediaFiles, currentImageIndex, removeMedia]);
  // constant array of length media.length with random numbers between 100 and 1000

  const renderAdTemplate = (format = selectedFormat) => {
    const adContent = campaignPreview?.ad_content;
    const title = hasCatalog ? `${catalogProducts[currentImageIndex]?.name} - ${catalogProducts[currentImageIndex]?.price}` : adContent?.headlines?.[0] || 'Título del producto';
    const description = adContent?.descriptions?.[0] || 'Descripción del producto';
    
    // Get current media URL and type
    let currentMediaUrl = null;
    let currentMediaType = null;
    if (hasCatalog && catalogImages.length > 0) {
      currentMediaUrl = catalogImages[currentImageIndex]?.image_url;
      currentMediaType = 'image';
    } else if (mediaFiles.length > 0) {
      currentMediaUrl = mediaFiles[currentImageIndex]?.url;
      // Detect media type based on file extension
      const url = mediaFiles[currentImageIndex]?.url || '';
      const extension = url.split('.').pop()?.toLowerCase();
      if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
        currentMediaType = 'video';
      } else {
        currentMediaType = 'image';
      }
    }

    switch (format) {
      case 'instagram_feed':
        return (
          <InstagramFeedPreview
            campaignPreview={campaignPreview}
            user={user}
            hasCatalog={hasCatalog}
            mediaUrl={currentMediaUrl}
            hasMedia={Boolean(currentMediaUrl)}
            loadingMedia={loadingMedia}
            uploadingMedia={uploadingMedia}
            loadingImage={loadingImage}
            deletingMedia={deletingMedia}
            currentImageIndex={currentImageIndex}
            mediaInputRef={mediaInputRef}
            handleImageSelection={handleImageSelection}
            removeCurrentImage={removeCurrentImage}
            editingHeadline={editingHeadline}
            editingDescription={editingDescription}
            editHeadlineText={editHeadlineText}
            editDescriptionText={editDescriptionText}
            isEditMode={isEditMode}
            handleEditHeadline={handleEditHeadline}
            handleEditDescription={handleEditDescription}
            handleSaveHeadline={handleSaveHeadline}
            handleSaveDescription={handleSaveDescription}
            handleCancelEdit={handleCancelEdit}
            setEditHeadlineText={setEditHeadlineText}
            setEditDescriptionText={setEditDescriptionText}
            likes={likes[currentImageIndex] || 1234}
            title={title}
            description={description}
            mediaType={currentMediaType}
          />
        );

      case 'story_format':
        return (
          <StoryPreview
            campaignPreview={campaignPreview}
            user={user}
            hasCatalog={hasCatalog}
            mediaUrl={currentMediaUrl}
            hasMedia={Boolean(currentMediaUrl)}
            loadingMedia={loadingMedia}
            uploadingMedia={uploadingMedia}
            loadingImage={loadingImage}
            deletingMedia={deletingMedia}
            imageOrientation={imageOrientation}
            mediaInputRef={mediaInputRef}
            handleImageSelection={handleImageSelection}
            removeCurrentImage={removeCurrentImage}
            mediaType={currentMediaType}
          />
        );

      case 'whatsapp_conversation':
        return (
          <WhatsAppConversationPreview
            campaignPreview={campaignPreview}
            user={user}
          />
        );

      default:
        return null;
    }
  };

  const handleEditHeadline = (index, text) => {
    setEditingHeadline(index);
    setEditHeadlineText(text);
  };

  const handleEditDescription = (index, text) => {
    setEditingDescription(index);
    setEditDescriptionText(text);
  };

  const handleSaveHeadline = (index) => {
    // Actualizar el headline en campaignPreview
    if (campaignPreview?.ad_content?.headlines) {
      const updatedHeadlines = [...campaignPreview.ad_content.headlines];
      updatedHeadlines[index] = editHeadlineText;
      
      // Actualizar el objeto campaignPreview
      campaignPreview.ad_content.headlines = updatedHeadlines;
    }
    
    setEditingHeadline(null);
    setEditHeadlineText('');
  };

  const handleSaveDescription = (index) => {
    // Actualizar la description en campaignPreview
    if (campaignPreview?.ad_content?.descriptions) {
      const updatedDescriptions = [...campaignPreview.ad_content.descriptions];
      updatedDescriptions[index] = editDescriptionText;
      
      // Actualizar el objeto campaignPreview
      campaignPreview.ad_content.descriptions = updatedDescriptions;
    }
    
    setEditingDescription(null);
    setEditDescriptionText('');
  };

  const handleCancelEdit = () => {
    setEditingHeadline(null);
    setEditingDescription(null);
    setEditHeadlineText('');
    setEditDescriptionText('');
  };

  // Funciones de edición de campos de campaña
  const handleEditField = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || '');
    setValidationError('');
  };

  const handleSaveField = (field) => {
    let isValid = true;
    let errorMessage = '';

    // Validaciones específicas por campo
    switch (field) {
      case 'budget':
        const budgetValue = parseFloat(editValue);
        if (isNaN(budgetValue) || budgetValue < 1) {
          isValid = false;
          errorMessage = 'El presupuesto diario debe ser al menos 1 USD';
        }
        break;
      case 'start_date':
        const startDate = new Date(editValue);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate < today) {
          isValid = false;
          errorMessage = 'La fecha de inicio no puede ser anterior a hoy';
        }
        break;
      case 'end_date':
        const endDate = new Date(editValue);
        const startDateForEnd = campaignPreview?.start_date ? new Date(campaignPreview.start_date) : new Date();
        if (endDate <= startDateForEnd) {
          isValid = false;
          errorMessage = 'La fecha de finalización debe ser posterior a la fecha de inicio';
        }
        break;
      default:
        break;
    }

    if (!isValid) {
      setValidationError(errorMessage);
      return;
    }

    // Actualizar el campo en campaignPreview
    if (campaignPreview) {
      switch (field) {
        case 'name':
          campaignPreview.name = editValue;
          break;
        case 'budget':
          campaignPreview.budget_amount_micros = Math.round(parseFloat(editValue) * 1000000);
          break;
        case 'start_date':
          campaignPreview.start_date = editValue;
          break;
        case 'end_date':
          campaignPreview.end_date = editValue;
          break;
        case 'whatsapp_number':
          campaignPreview.whatsapp_number = editValue;
          break;
        case 'whatsapp_message':
          campaignPreview.whatsapp_message = editValue;
          break;
        default:
          break;
      }
    }

    setEditingField(null);
    setEditValue('');
    setValidationError('');
  };

  const handleCancelCampaignEdit = () => {
    setEditingField(null);
    setEditValue('');
    setValidationError('');
  };

  const handleAddLocation = (country) => {
    if (!editLocations.find(loc => loc.code === country.code)) {
      setEditLocations([...editLocations, country]);
    }
    setShowLocationDropdown(false);
  };

  const handleRemoveLocation = (countryCode) => {
    setEditLocations(editLocations.filter(loc => loc.code !== countryCode));
  };

  const handleSaveLocations = () => {
    if (editLocations.length === 0) {
      setValidationError('Debe seleccionar al menos una ubicación');
      return;
    }
    
    if (campaignPreview) {
      campaignPreview.targeting = {
        ...campaignPreview.targeting,
        locations: editLocations.map(loc => ({ name: loc.name, code: loc.code }))
      };
    }
    
    setEditingField(null);
    setValidationError('');
  };

      return (
        <div className="bg-white rounded-xl px-4 md:px-6 space-y-4 w-full max-w-2xl mx-auto relative overflow-y-auto max-h-screen">


      {/* Ad Templates Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setIsEditMode(!isEditMode)}
          // todo: add this to the preview header when we can edit
          className={`hidden p-2 rounded-lg transition-colors ${
            isEditMode
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={isEditMode ? 'Salir del modo edición' : 'Entrar al modo edición'}
        >
          <Pencil className="w-4 h-4" />
        </button>
      </div>

            {/* Format Selector - Mobile Only */}
      <div className="lg:hidden flex justify-center space-x-2 overflow-x-auto pb-2">
        {adFormats.map((format) => (
          <button
            key={format.id}
            onClick={() => setSelectedFormat(format.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedFormat === format.id
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span>{format.icon}</span>
            <span>{format.name}</span>
          </button>
        ))}
      </div>

      {/* Ad Templates - Desktop: Show both, Mobile: Show selected */}
      {!isWhatsAppCampaign && (
        <div className="hidden lg:grid lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Feed Preview - Desktop Only */}
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium text-gray-600 mb-2">📷 Feed</div>
            <div className="flex justify-center">
              {renderAdTemplate('instagram_feed')}
            </div>
          </div>

          {/* Story Preview - Desktop Only (or WhatsApp when isWhatsAppCampaign) */}
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium text-gray-600 mb-2">📱 Story</div>
            <div className="flex justify-center">
              {renderAdTemplate('story_format')}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Campaign Layout - Desktop Only */}
      {isWhatsAppCampaign && (
        <div className="hidden lg:grid lg:grid-cols-2 gap-4 lg:gap-6">
          {/* First column: Feed/Story with switch */}
          <div className="flex flex-col items-center">
            <div className="flex items-center space-x-2 mb-2">
              <button
                onClick={() => setSelectedFormat('instagram_feed')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  selectedFormat === 'instagram_feed'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📷 Feed
              </button>
              <button
                onClick={() => setSelectedFormat('story_format')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  selectedFormat === 'story_format'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📱 Story
              </button>
            </div>
            <div className="flex justify-center">
              {renderAdTemplate()}
            </div>
          </div>

          {/* Second column: WhatsApp conversation */}
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium text-gray-600 mb-2">💬 WhatsApp</div>
            <div className="flex justify-center">
              <WhatsAppConversationPreview
                campaignPreview={campaignPreview}
                user={user}
              />
            </div>
          </div>
        </div>
      )}

      {/* Single Ad Template - Mobile Only */}
      <div className="lg:hidden flex justify-center">
        {renderAdTemplate()}
      </div>

      {/* Image Slider - Show when there are multiple images (catalog or uploaded) */}
      {((hasCatalog && catalogImages.length > 1) || (!hasCatalog && mediaFiles.length > 1)) && (
        <div className="flex flex-col items-center space-y-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={prevImage}
              disabled={loadingMedia || loadingImage || deletingMedia}
              className={`w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors ${
                (loadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Elemento anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <div className="text-sm text-gray-600 font-medium">
              {currentImageIndex + 1} de {hasCatalog ? catalogImages.length : mediaFiles.length}
            </div>
            
            <button
              onClick={nextImage}
              disabled={loadingMedia || loadingImage || deletingMedia}
              className={`w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors ${
                (loadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Siguiente elemento"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {/* Image dots indicator */}
          <div className="flex space-x-2">
            {(hasCatalog ? catalogImages : mediaFiles).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                disabled={loadingMedia || loadingImage || deletingMedia}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentImageIndex 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300 hover:bg-gray-400'
                } ${
                  (loadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={`Ir a elemento ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Campaign Configuration Section */}
      <div className="flex-shrink-0 px-3 md:px-4 py-3 !mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100 mb-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
        
        <div className="grid grid-cols-2 gap-2 md:gap-3 text-sm">
          {/* Nombre de la campaña */}
          <div>
            <span className="text-gray-600 font-medium">Nombre de la campaña:</span>
            {editingField === 'name' ? (
              <div className="mt-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full p-2 border border-blue-300 rounded text-sm"
                  autoFocus
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => handleSaveField('name')}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelCampaignEdit}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                <span>{campaignPreview?.name || 'Campaña sin nombre'}</span>
                {isEditMode && (
                  <button
                    onClick={() => handleEditField('name', campaignPreview?.name)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Plataforma */}
          <div>
            <span className="text-gray-600 font-medium">¿Dónde va a aparecer tu anuncio?</span>
            <div className="font-semibold text-gray-900 mt-1 flex items-center">
              En Instagram y Facebook <span className="ml-1">💙</span>
            </div>
          </div>

          {/* Presupuesto diario */}
          <div>
            <span className="text-gray-600 font-medium">Presupuesto diario:</span>
            {editingField === 'budget' ? (
              <div className="mt-1">
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full p-2 border border-blue-300 rounded text-sm"
                  placeholder="1.00"
                  autoFocus
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => handleSaveField('budget')}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelCampaignEdit}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                <span>
                  {campaignPreview?.budget_amount_micros 
                    ? `${campaignPreview.budget_amount_micros / 1000000} ${campaignPreview.currency || 'USD'}`
                    : 'No definido'
                  }
                </span>
                {isEditMode && (
                  <button
                    onClick={() => handleEditField('budget', campaignPreview?.budget_amount_micros ? (campaignPreview.budget_amount_micros / 1000000).toString() : '')}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Ubicación */}
          <div>
            <span className="text-gray-600 font-medium">Ubicación:</span>
            {editingField === 'locations' ? (
              <div className="mt-1">
                <div className="space-y-2">
                  {editLocations.map((location) => (
                    <div key={location.code} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="text-sm">{location.name}</span>
                      <button
                        onClick={() => handleRemoveLocation(location.code)}
                        className="p-1 text-red-500 hover:text-red-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                    className="w-full p-2 border border-dashed border-gray-300 rounded text-sm text-gray-600 hover:border-blue-400"
                  >
                    + Agregar país
                  </button>
                  {showLocationDropdown && (
                    <div className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {countries.map((country) => (
                        <button
                          key={country.code}
                          onClick={() => handleAddLocation(country)}
                          className="w-full p-2 text-left hover:bg-gray-100 text-sm"
                        >
                          {country.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={handleSaveLocations}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelCampaignEdit}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                <span>
                  {campaignPreview?.targeting?.locations?.join(', ')}
                </span>
                {isEditMode && (
                  <button
                    onClick={() => {
                      setEditingField('locations');
                      setEditLocations(campaignPreview?.targeting?.locations || [{ code: 'UY', name: 'Uruguay' }]);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Fecha de inicio */}
          <div>
            <span className="text-gray-600 font-medium">Fecha de inicio:</span>
            {editingField === 'start_date' ? (
              <div className="mt-1">
                <input
                  type="date"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-2 border border-blue-300 rounded text-sm"
                  autoFocus
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => handleSaveField('start_date')}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelCampaignEdit}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                <span>
                  {formatDateString(campaignPreview?.start_date)}
                </span>
                {isEditMode && (
                  <button
                    onClick={() => handleEditField('start_date', campaignPreview?.start_date || '')}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Fecha de finalización */}
          <div>
            <span className="text-gray-600 font-medium">Fecha de finalización:</span>
            {editingField === 'end_date' ? (
              <div className="mt-1">
                <input
                  type="date"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  min={campaignPreview?.start_date || new Date().toISOString().split('T')[0]}
                  className="w-full p-2 border border-blue-300 rounded text-sm"
                  autoFocus
                />
                <div className="flex gap-1 mt-1">
                  <button
                    onClick={() => handleSaveField('end_date')}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelCampaignEdit}
                    className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                <span>
                  {formatDateString(campaignPreview?.end_date)}
                </span>
                {isEditMode && (
                  <button
                    onClick={() => handleEditField('end_date', campaignPreview?.end_date || '')}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* WhatsApp Number - Only for WhatsApp campaigns */}
          {isWhatsAppCampaign && (
            <div>
              <span className="text-gray-600 font-medium">Número de WhatsApp:</span>
              {editingField === 'whatsapp_number' ? (
                <div className="mt-1">
                  <input
                    type="tel"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full p-2 border border-blue-300 rounded text-sm"
                    placeholder="+598 99 123 456"
                    autoFocus
                  />
                  <div className="flex gap-1 mt-1">
                    <button
                      onClick={() => handleSaveField('whatsapp_number')}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      ✓
                    </button>
                    <button
                      onClick={handleCancelCampaignEdit}
                      className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                  <span>
                    {campaignPreview?.meta_settings?.whatsapp_settings?.whatsapp_phone_number || 'No definido'}
                  </span>
                  {isEditMode && (
                    <button
                      onClick={() => handleEditField('whatsapp_number', campaignPreview?.meta_settings?.whatsapp_settings?.whatsapp_phone_number || '')}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Autofill Message - Only for WhatsApp campaigns */}
          {isWhatsAppCampaign && (
            <div>
              <span className="text-gray-600 font-medium">Mensaje por defecto:</span>
              {editingField === 'whatsapp_message' ? (
                <div className="mt-1">
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full p-2 border border-blue-300 rounded text-sm resize-none"
                    placeholder="¡Hola! Me gustaría saber más sobre..."
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-1 mt-1">
                    <button
                      onClick={() => handleSaveField('whatsapp_message')}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      ✓
                    </button>
                    <button
                      onClick={handleCancelCampaignEdit}
                      className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <div className="font-semibold text-gray-900 mt-1 flex items-center justify-between">
                  <span className="text-sm">
                    {campaignPreview?.meta_settings?.whatsapp_settings?.autofill_message || '¡Hola! Me gustaría saber más sobre la promoción'}
                  </span>
                  {isEditMode && (
                    <button
                      onClick={() => handleEditField('whatsapp_message', campaignPreview?.whatsapp_message || '¡Hola! Me gustaría saber más sobre la promoción')}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mensaje de error de validación */}
        {validationError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
            {validationError}
          </div>
        )}
      </div>



      {/* Upload Modal */}
      {/* Removed as per edit hint */}

      {/* Hidden file input */}
      <input
        ref={mediaInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
};

export default MetaPreview;
